import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaStore, FaMapMarkerAlt, FaPhone, FaFileAlt,
  FaStar, FaShieldAlt, FaArrowRight, FaExclamationTriangle,
  FaClipboardCheck, FaInfoCircle, FaHistory, FaCommentDots, FaUserShield
} from 'react-icons/fa';
import PublicBackMethods from './PublicBackMethods';

const PublicEstablishmentDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { public_request } = PublicBackMethods();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const response = await public_request.get(`/public/account/establishments/${id}/dashboard`);
        setData(response.data);
      } catch (error) {
        console.error("Error fetching details:", error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchDetails();
  }, [id]);

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
      <div className="spinner-grow text-primary" role="status">
        <span className="visually-hidden">جاري التحميل...</span>
      </div>
    </div>
  );

  if (!data) return (
    <div className="container mt-5 text-end">
      <div className="alert alert-danger shadow-sm rounded-4 d-flex align-items-center" dir="rtl">
        <FaExclamationTriangle className="ms-3 fs-3" />
        <div>لم يتم العثور على بيانات المنشأة المطلوبة.</div>
      </div>
      <button className="btn btn-outline-primary rounded-pill mt-3" onClick={() => navigate(-1)}>
        <FaArrowRight className="ms-2" /> العودة للخلف
      </button>
    </div>
  );

  const { establishment, rating, visits, violations, complaints } = data;

  return (
    <div className="container py-5 text-end" dir="rtl">
      {/* زر الرجوع */}
      <div className="mb-4 d-flex justify-content-between align-items-center">
        <button className="btn btn-light shadow-sm rounded-pill px-4 d-flex align-items-center fw-bold text-secondary" onClick={() => navigate(-1)}>
          <FaArrowRight className="ms-2" /> رجوع
        </button>
        <div className="badge bg-white shadow-sm text-primary p-2 px-3 rounded-pill border">
          <FaInfoCircle className="ms-2" /> ملف المنشأة الرقابي
        </div>
      </div>

      {/* Header Card */}
      <div className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4">
        <div className="p-4 text-white" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
          <div className="row align-items-center">
            <div className="col-md-8">
              <div className="d-flex align-items-center mb-2">
                <div className="bg-white text-dark rounded-circle p-3 ms-3 shadow-sm">
                  <FaStore size={30} />
                </div>
                <div>
                  <h1 className="fw-bold mb-0 h3">{establishment.name}</h1>
                  <span className="opacity-75">{establishment.facilityType} • {establishment.salesType}</span>
                </div>
              </div>
            </div>
            <div className="col-md-4 text-md-start mt-3 mt-md-0">
              <div className="bg-white bg-opacity-10 rounded-4 p-3 border border-white border-opacity-25 d-inline-block text-center">
                <div className="fw-bold h2 mb-0 text-warning">{rating.avg.toFixed(1)} <FaStar className="small" /></div>
                <div className="small opacity-75">بناءً على {rating.count} تقييم</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="row g-3 mb-4 text-center">
        <div className="col-4">
          <div className="card border-0 shadow-sm rounded-4 p-3 h-100 bg-white">
            <FaClipboardCheck className="text-primary mb-2 mx-auto" size={20} />
            <div className="h4 fw-bold mb-0">{visits.length}</div>
            <div className="small text-muted">زيارات</div>
          </div>
        </div>
        <div className="col-4">
          <div className="card border-0 shadow-sm rounded-4 p-3 h-100 bg-white">
            <FaExclamationTriangle className="text-danger mb-2 mx-auto" size={20} />
            <div className="h4 fw-bold mb-0">{violations.length}</div>
            <div className="small text-muted">مخالفات</div>
          </div>
        </div>
        <div className="col-4">
          <div className="card border-0 shadow-sm rounded-4 p-3 h-100 bg-white">
            <FaCommentDots className="text-success mb-2 mx-auto" size={20} />
            <div className="h4 fw-bold mb-0">{complaints.length}</div>
            <div className="small text-muted">شكاوى</div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <ul className="nav nav-pills nav-fill mb-4 bg-white p-2 rounded-4 shadow-sm border">
        {['overview', 'visits', 'violations', 'complaints'].map((tab) => (
          <li className="nav-item" key={tab}>
            <button
              className={`nav-link rounded-pill fw-bold ${activeTab === tab ? 'active btn-primary' : 'text-secondary'}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'overview' && 'المعلومات العامة'}
              {tab === 'visits' && 'سجل الزيارات'}
              {tab === 'violations' && 'سجل الضبوط'}
              {tab === 'complaints' && 'الشكاوى'}
            </button>
          </li>
        ))}
      </ul>

      {/* Tab Content */}
      <div className="tab-content">

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="row g-4 animate-fade-in">
            <div className="col-md-6">
              <div className="card border-0 shadow-sm rounded-4 p-4 h-100">
                <h5 className="fw-bold mb-4 text-primary border-bottom pb-2"><FaMapMarkerAlt className="ms-2" /> الموقع والتواصل</h5>
                <div className="mb-3"><strong>المحافظة:</strong> {establishment.governorate}</div>
                <div className="mb-3"><strong>المنطقة:</strong> {establishment.city} {establishment.district && `(${establishment.district})`}</div>
                <div className="mb-3"><strong>العنوان:</strong> {establishment.addressText || "غير محدد"}</div>
                <div className="mb-0"><strong>الهاتف:</strong> {establishment.phone || "غير متوفر"}</div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-0 shadow-sm rounded-4 p-4 h-100 text-center d-flex flex-column justify-content-center">
                <FaShieldAlt className={`fs-1 mb-3 mx-auto ${violations.length > 0 ? 'text-warning' : 'text-success'}`} />
                <h5 className="fw-bold">الوضع القانوني</h5>
                <p className="text-muted">
                  {violations.length > 0
                    ? "توجد مخالفات مسجلة بحق هذه المنشأة، يرجى توخي الحذر ومراجعة التقييمات."
                    : "هذه المنشأة سجلها نظيف من الضبوط الجسيمة حالياً."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Visits Tab */}
        {activeTab === 'visits' && (
          <div className="animate-fade-in">
            {visits.length > 0 ? visits.map((visit, idx) => (
              <div className="card border-0 shadow-sm rounded-4 mb-3 p-3 border-end border-primary border-5" key={idx}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="fw-bold mb-1">زيارة {visit.visitType}</h6>
                    <small className="text-muted"><FaHistory className="ms-1" /> {new Date(visit.visitDate).toLocaleDateString('ar-SY')}</small>
                  </div>
                  <div className="text-primary fw-bold">#{visit.visitId}</div>
                </div>
                <p className="mt-2 mb-0 bg-light p-2 rounded-3 small italic">"{visit.visitReport}"</p>
              </div>
            )) : <p className="text-center text-muted py-5">لا توجد زيارات مسجلة</p>}
          </div>
        )}

        {/* Violations Tab */}
        {activeTab === 'violations' && (
          <div className="animate-fade-in">
            {violations.length > 0 ? violations.map((violation, idx) => (
              <div className="card border-0 shadow-sm rounded-4 mb-3 p-3 border-end border-danger border-5" key={idx}>
                <div className="d-flex justify-content-between mb-2">
                  <span className="badge bg-danger">مخالفة رقم {violation.reportNumber}</span>
                  <small className="text-muted">{new Date(violation.reportDate).toLocaleDateString('ar-SY')}</small>
                </div>
                <h6 className="fw-bold text-danger">ملخص الضبط:</h6>
                <p className="mb-0">{violation.violationSummary}</p>
              </div>
            )) : <div className="alert alert-success text-center rounded-4">سجل نظيف! لا توجد مخالفات مسجلة.</div>}
          </div>
        )}

        {/* Complaints Tab */}
        {activeTab === 'complaints' && (
          <div className="animate-fade-in">
            {complaints.length > 0 ? complaints.map((complaint, idx) => (
              <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden" key={idx}>
                <div className="p-3 bg-light border-bottom d-flex justify-content-between">
                  <small className="text-muted">{new Date(complaint.createdAtUtc).toLocaleTimeString('ar-SY')} - {new Date(complaint.createdAtUtc).toLocaleDateString('ar-SY')}</small>
                  {complaint.isProcessed && <span className="badge bg-success-subtle text-success rounded-pill">تمت المعالجة</span>}
                </div>
                <div className="p-3">
                  <h6 className="fw-bold mb-2 d-flex align-items-center"><FaCommentDots className="ms-2 text-secondary" /> نص الشكوى:</h6>
                  <p className="mb-3 text-dark">{complaint.complaintText}</p>

                  {complaint.staffResponse && (
                    <div className="bg-primary bg-opacity-10 p-3 rounded-4 mt-2">
                      <h6 className="fw-bold text-primary mb-2 d-flex align-items-center">
                        <FaUserShield className="ms-2" /> رد الجهة الرقابية:
                      </h6>
                      <p className="mb-0 small">{complaint.staffResponse}</p>
                    </div>
                  )}
                </div>
              </div>
            )) : <p className="text-center text-muted py-5">لا توجد شكاوى عامة منشورة.</p>}
          </div>
        )}

      </div>

      <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-in-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                .nav-link { color: #64748b; transition: all 0.3s ease; border: 1px solid transparent !important; margin: 0 2px; }
                .nav-pills .nav-link.active { background-color: #0d6efd !important; color: white !important; box-shadow: 0 4px 12px rgba(13, 110, 253, 0.2); }
                .hover-up:hover { transform: translateY(-3px); }
            `}</style>
    </div>
  );
};

export default PublicEstablishmentDetailsPage;